import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useDispatch } from 'react-redux';
import { useLoginMutation } from '../../store/api';
import { setCredentials } from '../../store/authSlice';
import { useTheme } from '../../theme/ThemeContext';
import { COLORS, getThemeStyles, globalStyles } from '../../theme/designSystem';
import { PassEncrypted } from '../../utils/helper';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export const LoginScreen = ({ navigation }: any) => {
  const [loginApi, { isLoading }] = useLoginMutation();
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { isDark } = useTheme();
  const theme = getThemeStyles(isDark);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: 'reception@aeroflow.com',
      password: 'reception',
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    setErrorMessage(null);
    try {
      
      const res = await loginApi({payload: PassEncrypted(data)}).unwrap();
      console.log(res)
      dispatch(
        setCredentials({
          user: res.role,
          accessToken: res.data,
          // refreshToken: res.refreshToken,
        }),
      );
    } catch (err: any) {
      setErrorMessage(
        err?.data?.message || 'Authentication failed. Please check your credentials.',
      );
    }
  };

  const setDemoCredentials = (role: 'admin' | 'receptionist') => {
    if (role === 'admin') {
      setValue('email', 'master@aeroflow.com');
      setValue('password', 'master');
    } else {
      setValue('email', 'reception@aeroflow.com');
      setValue('password', 'reception');
    }
  };

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      style={{ flex: 1, backgroundColor: theme.bg }}
    >
      <ScrollView
        contentContainerStyle={{ flexGrow: 1, justifyContent: 'center' }}
        style={{ flex: 1, backgroundColor: theme.bg }}
      >
        {/* Header Gradient */}
        <LinearGradient
          colors={['#0F172A', '#1E1B4B', '#2563EB']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.headerGradient}
        >
          <View style={styles.iconCircle}>
            <Text style={{ fontSize: 32 }}>📅</Text>
          </View>
          <Text style={styles.brandTitle}>Aeroflow Meeting HQ</Text>
          <Text style={styles.brandSubtitle}>
            Smart Meeting Scheduling & Approval System
          </Text>
        </LinearGradient>

        {/* Form Container */}
        <View style={styles.formOuterWrapper}>
          <View
            style={[
              globalStyles.card,
              {
                backgroundColor: theme.cardBg,
                borderColor: theme.border,
              },
            ]}
          >
            <Text style={[styles.cardTitle, { color: theme.textPrimary }]}>Sign In</Text>
            <Text style={[styles.cardSubtitle, { color: theme.textSecondary }]}>
              Enter credentials to access schedule management
            </Text>

            {errorMessage && (
              <View style={styles.errorBox}>
                <Text style={styles.errorText}>{errorMessage}</Text>
              </View>
            )}

            {/* Email Field */}
            <Text style={[styles.inputLabel, { color: theme.textPrimary }]}>Email Address</Text>
            <Controller
              control={control}
              name="email"
              render={({ field: { onChange, onBlur, value } }) => (
                <TextInput
                  onBlur={onBlur}
                  onChangeText={onChange}
                  value={value}
                  placeholder="admin@aeroflow.com"
                  placeholderTextColor={theme.textSecondary}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  style={[
                    globalStyles.input,
                    {
                      backgroundColor: theme.inputBg,
                      borderColor: theme.border,
                      color: theme.textPrimary,
                    },
                  ]}
                />
              )}
            />
            {errors.email && (
              <Text style={styles.fieldErrorText}>{errors.email.message}</Text>
            )}

            {/* Password Field */}
            <Text style={[styles.inputLabel, { color: theme.textPrimary, marginTop: 8 }]}>Password</Text>
            <Controller
              control={control}
              name="password"
              render={({ field: { onChange, onBlur, value } }) => (
                <TextInput
                  onBlur={onBlur}
                  onChangeText={onChange}
                  value={value}
                  placeholder="••••••••"
                  placeholderTextColor={theme.textSecondary}
                  secureTextEntry
                  style={[
                    globalStyles.input,
                    {
                      backgroundColor: theme.inputBg,
                      borderColor: theme.border,
                      color: theme.textPrimary,
                    },
                  ]}
                />
              )}
            />
            {errors.password && (
              <Text style={styles.fieldErrorText}>{errors.password.message}</Text>
            )}

            {/* Submit Button */}
            <TouchableOpacity
              onPress={handleSubmit(onSubmit)}
              disabled={isLoading}
              style={globalStyles.buttonPrimary}
            >
              {isLoading ? (
                <ActivityIndicator color="#ffffff" />
              ) : (
                <Text style={globalStyles.buttonText}>Sign In to Dashboard →</Text>
              )}
            </TouchableOpacity>

            {/* Demo Fill Switcher */}
            <View style={[styles.demoBox, { borderTopColor: theme.border }]}>
              <Text style={[styles.demoTitle, { color: theme.textSecondary }]}>
                ⚡ Quick Demo Credentials Fill:
              </Text>
              <View style={styles.demoBtnRow}>
                <TouchableOpacity
                  onPress={() => setDemoCredentials('admin')}
                  style={[styles.demoBadge, { backgroundColor: 'rgba(37, 99, 235, 0.15)', borderColor: COLORS.primary }]}
                >
                  <Text style={{ color: COLORS.primaryLight, fontSize: 12, fontWeight: '700' }}>
                    👑 Master Admin
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  onPress={() => setDemoCredentials('receptionist')}
                  style={[styles.demoBadge, { backgroundColor: 'rgba(124, 58, 237, 0.15)', borderColor: COLORS.secondary }]}
                >
                  <Text style={{ color: COLORS.secondaryLight, fontSize: 12, fontWeight: '700' }}>
                    💁 Receptionist
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  headerGradient: {
    paddingTop: Platform.OS === 'web' ? 40 : 60,
    paddingBottom: 32,
    paddingHorizontal: 24,
    borderBottomLeftRadius: 36,
    borderBottomRightRadius: 36,
    alignItems: 'center',
  },
  iconCircle: {
    width: 64,
    height: 64,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    alignItems: 'center',
    justify: 'center',
    marginBottom: 12,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.25)',
  },
  brandTitle: {
    color: '#FFFFFF',
    fontSize: 26,
    fontWeight: '800',
    textAlign: 'center',
    letterSpacing: -0.5,
  },
  brandSubtitle: {
    color: '#93C5FD',
    fontSize: 12,
    marginTop: 4,
    fontWeight: '600',
    textAlign: 'center',
  },
  formOuterWrapper: {
    paddingHorizontal: 20,
    paddingVertical: 24,
    width: '100%',
    maxWidth: 480,
    alignSelf: 'center',
  },
  cardTitle: {
    fontSize: 22,
    fontWeight: '800',
    marginBottom: 4,
  },
  cardSubtitle: {
    fontSize: 12,
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 12,
    fontWeight: '700',
    marginBottom: 6,
  },
  fieldErrorText: {
    color: COLORS.danger,
    fontSize: 11,
    marginTop: -8,
    marginBottom: 10,
    fontWeight: '600',
  },
  errorBox: {
    padding: 12,
    borderRadius: 12,
    backgroundColor: COLORS.dangerBg,
    borderWidth: 1,
    borderColor: COLORS.danger,
    marginBottom: 16,
  },
  errorText: {
    color: COLORS.danger,
    fontSize: 12,
    fontWeight: '600',
  },
  forgotBtn: {
    alignSelf: 'flex-end',
    marginBottom: 20,
    marginTop: 2,
  },
  forgotText: {
    color: COLORS.primary,
    fontSize: 12,
    fontWeight: '700',
  },
  demoBox: {
    marginTop: 24,
    paddingTop: 16,
    borderTopWidth: 1,
  },
  demoTitle: {
    fontSize: 11,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 10,
  },
  demoBtnRow: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  demoBadge: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 12,
    borderWidth: 1,
    marginHorizontal: 4,
  },
});
